import { useEffect, useState } from "react";

import { RotateCcw } from "lucide-react";
import { useDispatch } from "react-redux";

import { startTranscriptionSummaryTask } from "@/redux/transcription-summary";
import {
  currentSubmission,
  discardSavedOwnership,
  recoverOwnership,
  releaseRecording,
} from "@/services/recordingOwnership";
import type { store as appStore } from "@/store";
import { showError } from "@/utils/notification";

import { ConfirmationModal } from "@components/ConfirmationModal";
import { Button } from "@components/ui/button";

import { buildSummarizeFormData } from "@pages/ReportStudio/AudioTranscriber/components/summarize-form-data";
import {
  type RecoveredRecording,
  clearPersistedRecording,
  findOrphanedRecording,
} from "@pages/ReportStudio/AudioTranscriber/hooks/recordingPersistence";

type RecordingRecoveryPromptProps = {
  /**
   * The case currently on screen. A recovered recording is ONLY ever surfaced
   * when it matches this — a hard access-control gate, not a display nicety.
   * Case number and examinee name are PHI; mounting this at app root and
   * checking on every login would disclose "case X's recording was
   * interrupted" to ANY authenticated user on ANY page, before any backend
   * ownership check ever ran — a real risk on a shared clinic workstation.
   *
   * The route param alone is NOT sufficient authorization — a raw URL param
   * proves nothing about access. The caller (AudioTranscript.tsx) is
   * responsible for only passing a real value here once the page's own
   * case-read query has actually succeeded for the current user (the backend
   * enforces `can_read_case` on that fetch), and for withholding it entirely
   * (pass `undefined`) otherwise.
   */
  caseNumber: string | undefined;
};

/**
 * SCRIBE-05: a hard reload or browser crash mid-recording destroys the live
 * MediaRecorder and every chunk held in the in-memory buffer (recordingBuffer.ts)
 * with it — silently, with no error surfaced. `recordingPersistence.ts` durably
 * mirrors captured audio to IndexedDB as insurance against exactly that, but the
 * audio being safe on disk is useless if nobody ever offers it back to the user.
 * Mounted on the scribe page (not app root — see `caseNumber` above), this
 * checks for a recording an earlier tab never finished on THIS case, and
 * offers to send the captured audio for transcription (or discard it).
 *
 * Deliberately separate from AudioTranscript's "Restore unsaved dictation?"
 * modal: that one only recovers the live TEXT transcript from localStorage and
 * says nothing about audio, so a user restoring from it could reasonably
 * believe their whole session — audio included — was recovered when it wasn't.
 * This prompt is explicit that the live session itself cannot resume.
 */
export const RecordingRecoveryPrompt = ({
  caseNumber,
}: RecordingRecoveryPromptProps) => {
  const dispatch = useDispatch<typeof appStore.dispatch>();
  const [recovered, setRecovered] = useState<RecoveredRecording | null>(null);
  const [isSending, setIsSending] = useState(false);

  useEffect(() => {
    if (!caseNumber) return;
    let mounted = true;
    findOrphanedRecording(caseNumber)
      .then((found) => {
        if (mounted) setRecovered(found);
      })
      .catch(() => {
        /* best-effort — surfacing nothing is the safe failure mode */
      });
    return () => {
      mounted = false;
    };
  }, [caseNumber]);

  if (!recovered) return null;

  // examineeName can still be missing (e.g. the dock hadn't resolved it yet
  // when the session began) — caseNumber itself is guaranteed by the lookup.
  const canSend = !!recovered.examineeName;

  const handleDiscard = async () => {
    try {
      await discardSavedOwnership(recovered.caseNumber, recovered.submissionId);
      await clearPersistedRecording(recovered.sessionId);
    } catch {
      // The local copy is kept, so the doctor can retry or download it.
      showError("Couldn't discard the recording. Try again.", dispatch);
      return;
    }
    setRecovered(null);
  };

  const handleSend = async () => {
    if (!canSend) return;
    setIsSending(true);
    try {
      if (!recovered.submissionId || !recovered.caseNumber)
        throw new Error(
          "This older recording must be downloaded before starting again."
        );
      await recoverOwnership(recovered.caseNumber, recovered.submissionId);
      const formData = buildSummarizeFormData({
        submissionId: recovered.submissionId,
        speakerHints: recovered.speakerHints,
        // Safe to assert: this component only ever looks up
        // `findOrphanedRecording(caseNumber)` with the definite, non-null
        // `caseNumber` prop, so a match's own caseNumber is that same value.
        caseNumber: recovered.caseNumber as string,
        examineeName: recovered.examineeName as string,
        audioBlob: recovered.blob,
      });
      await dispatch(startTranscriptionSummaryTask(formData));
      await clearPersistedRecording(recovered.sessionId);
      releaseRecording();
      setRecovered(null);
    } catch {
      // The thunk already surfaced its own error toast. Leave the persisted
      // recording in place so the user can retry instead of losing it here too.
      // Retrying re-claims ownership, so don't leave this tab marked busy.
      releaseRecording();
    } finally {
      setIsSending(false);
    }
  };

  return (
    <ConfirmationModal
      type="info"
      icon={<RotateCcw className="size-8 text-primary" />}
      title="Recording interrupted"
      body={
        <Button
          variant="simple-outline"
          onClick={() => {
            const url = URL.createObjectURL(recovered.blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = "saved-recording.webm";
            link.click();
            setTimeout(() => URL.revokeObjectURL(url), 1000);
          }}
        >
          Download recording
        </Button>
      }
      description={
        canSend
          ? `A recording for case ${recovered.caseNumber} was interrupted or didn't finish sending for transcription — likely because of a page refresh, crash or failed upload. The audio captured was saved. Send it for transcription now, or discard it.`
          : "A recording for this case was interrupted before it finished, and we couldn't confirm the examinee name for it, so the captured audio can only be discarded."
      }
      confirmButtonText="Send for transcription"
      cancelButtonText="Discard"
      open={!!recovered}
      isLoading={isSending}
      confirmDisabled={!canSend || !recovered.submissionId}
      // Esc / outside click only closes it: the recording may be a finished one
      // whose upload failed (MDRS-551), so only the Discard button deletes it.
      onOpenChange={(open) => {
        if (!open && !isSending && currentSubmission() !== recovered?.submissionId)
          setRecovered(null);
      }}
      onConfirm={handleSend}
      onCancel={handleDiscard}
    />
  );
};
